import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { safeHttpUrl } from '@internal/backstage-plugin-common-react';
import { ANNOTATION_DAAS_ARGOCD_URL } from '@internal/backstage-plugin-daas-common';

import { type Entity, stringifyEntityRef } from '@backstage/catalog-model';
import type { TableProps } from '@backstage/core-components';
import type { CatalogTableRow } from '@backstage/plugin-catalog';
import { useStarredEntities } from '@backstage/plugin-catalog-react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { makeStyles } from 'tss-react/mui';

import { useDaaSEditableEntities } from '../../hooks';
import { daasDeleteUrl, daasEditUrl } from '../../utils/templateUrls';

const useStyles = makeStyles()(theme => ({
  actionIcon: {
    opacity: 1,
  },
  actionIconDisabled: {
    opacity: theme.palette.action.disabledOpacity,
  },
}));

type ActionIconClasses = ReturnType<typeof useStyles>['classes'];

const CHECKING_PERMISSIONS = 'Checking permissions…';

const argocdLinkAction = (row: CatalogTableRow) => {
  const url = safeHttpUrl(
    row.entity.metadata.annotations?.[ANNOTATION_DAAS_ARGOCD_URL],
  );
  return {
    icon: () => <LaunchIcon fontSize="small" />,
    tooltip: url ? 'Open in ArgoCD' : 'No ArgoCD URL',
    disabled: !url,
    onClick: () => {
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },
  };
};

type MutationActionOptions = {
  /** Used in the tooltip, and as the label of the enabled action. */
  verb: 'Edit' | 'Delete';
  Icon: typeof EditIcon;
  urlFor: (entity: Entity) => string;
};

/**
 * Builds the edit or the delete action.
 */
const createMutationAction = (
  editableRefs: Set<string>,
  permissionsLoading: boolean,
  navigate: (to: string) => void,
  classes: ActionIconClasses,
  { verb, Icon, urlFor }: MutationActionOptions,
) => {
  return (row: CatalogTableRow) => {
    const allowed =
      !permissionsLoading && editableRefs.has(stringifyEntityRef(row.entity));

    let tooltip: string;
    if (permissionsLoading) {
      tooltip = CHECKING_PERMISSIONS;
    } else if (allowed) {
      tooltip = verb;
    } else {
      tooltip = `You do not have permission to ${verb.toLowerCase()} this entity`;
    }

    return {
      icon: () => (
        <Icon
          fontSize="small"
          className={allowed ? classes.actionIcon : classes.actionIconDisabled}
        />
      ),
      tooltip,
      disabled: !allowed,
      onClick: () => {
        if (allowed) navigate(urlFor(row.entity));
      },
    };
  };
};

const createStarredAction = (
  isStarredEntity: ReturnType<typeof useStarredEntities>['isStarredEntity'],
  toggleStarredEntity: ReturnType<
    typeof useStarredEntities
  >['toggleStarredEntity'],
) => {
  return (row: CatalogTableRow) => {
    const starred = isStarredEntity(row.entity);

    return {
      icon: () =>
        starred ? (
          <StarIcon fontSize="small" color="warning" />
        ) : (
          <StarBorderIcon fontSize="small" />
        ),
      tooltip: starred ? 'Remove from favorites' : 'Add to favorites',
      onClick: () => {
        toggleStarredEntity(row.entity);
      },
    };
  };
};

/**
 * The per-row actions of the DaaS explorer table: open in ArgoCD, edit,
 * delete and star - in that order.
 */
export const useDaaSEntityActions =
  (): TableProps<CatalogTableRow>['actions'] => {
    const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
    const navigate = useNavigate();
    const { classes } = useStyles();

    const { editableRefs, loading: permissionsLoading } =
      useDaaSEditableEntities();

    // `CatalogTable` re-renders every row whenever `actions` changes identity,
    // so the array and the four closures inside it are built once per input.
    return useMemo(
      () => [
        argocdLinkAction,
        createMutationAction(
          editableRefs,
          permissionsLoading,
          navigate,
          classes,
          { verb: 'Edit', Icon: EditIcon, urlFor: daasEditUrl },
        ),
        createMutationAction(
          editableRefs,
          permissionsLoading,
          navigate,
          classes,
          { verb: 'Delete', Icon: DeleteIcon, urlFor: daasDeleteUrl },
        ),
        createStarredAction(isStarredEntity, toggleStarredEntity),
      ],
      [
        editableRefs,
        permissionsLoading,
        navigate,
        classes,
        isStarredEntity,
        toggleStarredEntity,
      ],
    );
  };
